import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  Grid,
  IconButton,
  Typography,
  CircularProgress,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  TableBody,
  DialogContentText,
  DialogActions,
  Box,
  Checkbox,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  DialogContent
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  StyledTable,
  TableRowStyled,
  TableRowColumn,
  CellC1,
  CellC2,
  TableCellStyled,
  FileUploadBtnFit,
  FileUploadSelectBtn,
  FileDesc,
  FileDataRowStyled
} from 'styles/GlobalStyle';
import {
  deleteFileAttachment,
  downloadFileAttachment,
  getFilesByOrderId,
  updateFileAttachment,
  uploadFileAttachment
} from 'services/order-service';
import ToastHandler from 'components/Toast-Handler/toast-handler';

const StyledDialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2),
  color: '#2196f3',
  letterSpacing: 1,
  textTransform: 'uppercase',
  position: 'relative',

  '& .closeButton': {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
}));

const StyledDialogContent = styled(MuiDialogContent)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const DialogTitle = ({ children, onClose, ...other }) => (
  <StyledDialogTitle {...other}>
    <Typography variant='h6'>{children}</Typography>
    {onClose && (
      <IconButton aria-label='close' className='closeButton' onClick={onClose} size='large'>
        <CloseIcon />
      </IconButton>
    )}
  </StyledDialogTitle>
);

const FilesModal = forwardRef(({ orderId, filesData, errorData, messageData, isOTR }, ref) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [files, setFiles] = useState(filesData);
  const [selectedFile, setSelectedFile] = useState({ name: 'No File Selected' });
  const [fileDescription, setFileDescription] = useState('');
  const [error, setError] = useState(errorData);
  const [message, setMessage] = useState(messageData);
  const [rowSelected, setRowSelected] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [invoiceAttachment, setInvoiceAttachment] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editFileDescription, setEditFileDescription] = useState('');
  const [editInvoiceAttachment, setEditInvoiceAttachment] = useState(false);

  const fileInput = useRef(null);

  useImperativeHandle(ref, () => ({
    onInit(data, err) {
      if (err) {
        setError(err.message);
      } else {
        setFiles(data);
      }
      setIsProcessing(false);
    }
  }));

  const handleRowClick = (e, uuid) => setRowSelected(uuid);
  const handleFileDesc = (e) => setFileDescription(e.target.value);

  const handleDelete = () => {
    setIsProcessing(true);
    deleteFileAttachment(orderId, rowSelected).then((res) => {
      if (res.status !== 200) setError('Failed to delete file.');
      setRowSelected('');
    }).catch(() => setError('Failed to delete file.'))
      .finally(() => loadFiles());
  };

  const handledownload = () => {
    downloadFileAttachment(rowSelected).then((res) => {
      if (res.status === 200 && res.data?.ok) {
        window.open(res.data.link, '_blank', 'noreferrer');
      } else {
        setError('Failed to download the file.');
      }
    }).catch(() => setError('Failed to download the file.'));
  };

  const handleFileInput = (e) => {
    const f = e.target.files[0];
    if (f && f.size < 5 * 1024 * 1024) {
      setSelectedFile(f);
      setError(null);
    } else {
      setSelectedFile({});
      setError('Invalid File or File is too big.');
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      const formData = new FormData();
      formData.append('fileDesc', fileDescription);
      formData.append('file', selectedFile);
      formData.append('orderId', orderId);
      formData.append('invoiceAttachable', invoiceAttachment);
      formData.append('isOTR', !!isOTR);
      setIsProcessing(true);
      uploadFileAttachment(formData).then(r => {
        if (r.status === 200 && r.data?.ok) {
          setMessage('Uploaded successfully.');
          loadFiles();
        } else {
          setError('Failed to upload the file.');
        }
      }).catch(() => setError('Failed to upload the file.'))
        .finally(() => {
          setIsProcessing(false);
          setFileDescription('');
          setSelectedFile({});
        });
    }
  };

  const handleUploadEdit = () => {
    if (selectedFile) {
      const formData = new FormData();
      formData.append('fileDesc', editFileDescription);
      formData.append('invoiceAttachable', editInvoiceAttachment);
      setIsProcessing(true);
      updateFileAttachment(formData, rowSelected).then(r => {
        if (r.status === 200 && r.data?.ok) {
          setMessage('Updated successfully.');
          loadFiles();
        } else {
          setError('Failed to update.');
        }
      }).catch(() => setError('Failed to update.'))
        .finally(() => {
          setIsProcessing(false);
          setEditFileDescription('');
          setEditInvoiceAttachment(false);
        });
    }
  };

  const loadFiles = () => {
    getFilesByOrderId(orderId, !!isOTR).then(res => {
      if (res.status === 200 && res.data) {
        setFiles(res.data);
      } else {
        setError('Unable to get load files.');
      }
    }).catch(e => setError('Unable to get load files.' + e))
      .finally(() => {
        setIsProcessing(false);
        setTimeout(() => {
          setError('');
          setMessage('');
        }, 10000);
      });
  };

  return (
    <React.Fragment>
      <Grid container spacing={4}>
        {isProcessing ? <CircularProgress style={{ margin: 'auto', marginLeft: '50%' }} /> : null}
        <Grid size={{xs: 12}}>
          <Box display={'flex'} justifyContent={'space-between'}>
            <StyledTable>
              <TableRowStyled>
                <CellC1>
                  <StyledTable>
                    <TableRowStyled>
                      <TableCellStyled style={{ flexBasis: '25%' }}>
                        <input type='file' onChange={handleFileInput} ref={fileInput} style={{ display: 'none' }} />
                        <FileUploadSelectBtn variant='outlined' onClick={() => fileInput.current && fileInput.current.click()} color='primary' disabled={(files.length >= 5) || editing}>
                          Select File
                        </FileUploadSelectBtn>
                      </TableCellStyled>
                      <TableCellStyled style={{ flexBasis: '75%' }}>
                        <Typography variant='subtitle2' style={{ color: '#000' }}>{selectedFile.name}</Typography>
                      </TableCellStyled>
                    </TableRowStyled>
                    <TableRowStyled>
                      <TableCellStyled style={{ flexBasis: '25%' }}>
                        <span>Attach to Invoice?</span>
                      </TableCellStyled>
                      <TableCellStyled style={{ flexBasis: '75%' }}>
                        <Checkbox onChange={(e) => setInvoiceAttachment(e.target.checked)} checked={invoiceAttachment} style={{ marginLeft: -11 }} />
                      </TableCellStyled>
                    </TableRowStyled>
                    <TableRowStyled>
                      <TableCellStyled style={{ flexBasis: '25%' }}>
                        <span>File Description</span>
                      </TableCellStyled>
                      <TableCellStyled style={{ flexBasis: '75%' }}>
                        <FileDesc
                          name='fileDescription'
                          onChange={handleFileDesc}
                          value={fileDescription}
                          required={true}
                          margin={'dense'}
                        />
                      </TableCellStyled>
                    </TableRowStyled>
                  </StyledTable>
                </CellC1>
                <CellC2>
                  <StyledTable>
                    <TableRowColumn>
                      <TableCellStyled style={{ alignContent: 'flex-end' }}>
                        <FileUploadBtnFit onClick={handleUpload} variant='contained' color='primary' disabled={(!selectedFile.type || !fileDescription || files.length >= 5)}>
                          Upload
                        </FileUploadBtnFit>
                      </TableCellStyled>
                    </TableRowColumn>
                  </StyledTable>
                </CellC2>
              </TableRowStyled>
            </StyledTable >
          </Box>
        </Grid>
        <Grid size={{xs: 12}}>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label='simple table'>
              <TableHead>
                <TableRow>
                  <TableCell component={'th'}>ID</TableCell>
                  <TableCell component={'th'} align='right'>File Name</TableCell>
                  <TableCell component={'th'} align='right'>Description</TableCell>
                  <TableCell component={'th'} align='right'>Invoice Attachable?</TableCell>
                  <TableCell component={'th'} align='right'>Size</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((row) => (
                  <FileDataRowStyled
                    selected={rowSelected === row.uuid}
                    tid={row.uuid}
                    onClick={(e) => handleRowClick(e, row.uuid)}
                    key={row.uuid}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell scope='row'>{row.id}</TableCell>
                    <TableCell align='right'>{row.fileName}</TableCell>
                    <TableCell align='right'>{rowSelected == row.uuid && editing ?
                      <FileDesc
                        name='fileDescription'
                        onChange={(e) => { setEditFileDescription(e.target.value) }}
                        value={editFileDescription}
                        required={true}
                        margin={'dense'}
                      />
                      : row.fileDesc}</TableCell>
                    <TableCell align='right'>{rowSelected == row.uuid && editing ?
                      <Checkbox onChange={(e) => setEditInvoiceAttachment(e.target.checked)} checked={editInvoiceAttachment} />
                      : (row.invoiceAttachable ? 'Yes' : 'No')
                    }
                    </TableCell>
                    <TableCell align='right'>{(row.size / 1024).toFixed(2) + ' KB'}</TableCell>
                  </FileDataRowStyled>)
                )}
              </TableBody>
            </Table>
            {
              files.length == 0 && (<div style={{ width: '100%', textAlign: 'center', padding: '15px' }}>
                <h5>No Files Found. Attach a file to the load to see files here.</h5>
              </div>)
            }
          </TableContainer>

        </Grid>
        <Grid size={{xs: 12}}>
          <Box
            display='flex'
            justifyContent='flex-end'
            p={2}
          >
            {
              editing && (<><Button onClick={() => { setEditing(false); handleUploadEdit(); }} variant='outlined' color='primary' style={{ marginRight: '2px' }}>
                OK
              </Button>
                <Button onClick={() => { setEditing(false); }} variant='outlined' color='secondary' style={{ marginRight: '2px' }}>
                  Cancel
                </Button></>)
            }
            <Button onClick={() => { setEditing(true); }} variant='outlined' color='primary' disabled={(!rowSelected) || editing} style={{ marginRight: '2px' }}>
              Edit
            </Button>
            <Button onClick={() => { setDeleteConfirmation(true); }} variant='outlined' color='primary' disabled={(!rowSelected) || editing} style={{ marginRight: '2px' }}>
              Delete
            </Button>
            <Button onClick={handledownload} variant='contained' color='primary' disabled={(!rowSelected) || editing} style={{ marginLeft: '2px' }}>
              Download
            </Button>
          </Box>
          {files.length >= 5 && <p style={{ display: 'inline-flex' }}>Max number of files have been attached to this load.</p>}
          <Dialog
            open={deleteConfirmation}
            onClose={() => { setDeleteConfirmation(false); }}
          >
            <DialogTitle id='alert-dialog-title'>
              {'Delete File?'}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id='alert-dialog-description'>
                {'Do you want to delete the selected file?'}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setDeleteConfirmation(false); }}>No</Button>
              <Button onClick={() => { setDeleteConfirmation(false); handleDelete(); }} autoFocus>
                Yes
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>
      </Grid>
      <Grid size={{xs: 12}}>
        <ToastHandler toast={{ message: error ? error : message }} type={error ? 'ERROR' : 'SUCCESS'} />
      </Grid>
    </React.Fragment>
  );
});

export default FilesModal;